import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { vacationsApi, type AbsenceKind, type UserVacation } from '@/api/vacations'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'

const ABSENCE_KINDS: AbsenceKind[] = ['vacation', 'break']

interface StudentAbsenceCardProps {
  studentId: number
  /** Обновить карточку ученика: период меняет и его уроки. */
  onChanged?: () => void
}

/**
 * Отпуск и каникулы ученика на его карточке у учителя.
 *
 * Период — тот же, что двигает автогенерацию уроков (`user_vacations`), а не
 * отдельный статус договора: договор ученика в отпуске остаётся активным, у
 * него живы абонемент и баланс, просто занятий в эти дни нет.
 *
 * Учитель период заводит, но не правит и не удаляет: снятие удаляет уже
 * сгенерированные уроки и ничего не восстанавливает, а у ученика бывает
 * несколько учителей. Убрать период может сам ученик или админ.
 */
export function StudentAbsenceCard({ studentId, onChanged }: StudentAbsenceCardProps) {
  const { t } = useTranslation()
  const [vacations, setVacations] = useState<UserVacation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [kind, setKind] = useState<AbsenceKind>('vacation')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setVacations(await vacationsApi.listForUser(studentId))
      setError(null)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
      setVacations([])
    } finally {
      setLoading(false)
    }
  }, [studentId, t])

  useEffect(() => {
    void load()
  }, [load])

  const today = new Date().toISOString().slice(0, 10)
  const current = vacations.find((item) => item.starts_on <= today && item.ends_on >= today) ?? null
  const upcoming = vacations.filter((item) => item.starts_on > today)

  const handleSave = async () => {
    if (startsOn === '' || endsOn === '') {
      setFormError(t('vacations.errors.periodRequired'))

      return
    }
    if (endsOn < startsOn) {
      setFormError(t('vacations.errors.endBeforeStart'))

      return
    }

    setSaving(true)
    setFormError(null)
    try {
      await vacationsApi.createForUser(studentId, { kind, starts_on: startsOn, ends_on: endsOn })
      setDialogOpen(false)
      setStartsOn('')
      setEndsOn('')
      await load()
      onChanged?.()
    } catch (e: unknown) {
      setFormError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
          <Typography variant="h6">{t('vacations.studentCard.title')}</Typography>
          <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => setDialogOpen(true)}>
            {t('vacations.studentCard.add')}
          </Button>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Skeleton variant="text" width={260} height={28} />
        ) : current ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color="warning" label={current.kind_label} />
            <Typography variant="body2" color="text.secondary">
              {formatDateDMY(current.starts_on)} — {formatDateDMY(current.ends_on)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('vacations.studentCard.none')}
          </Typography>
        )}

        {!loading && upcoming.length > 0 ? (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('vacations.studentCard.upcoming')}
            </Typography>
            {upcoming.map((item) => (
              <Typography key={item.id} variant="body2" color="text.secondary">
                {item.kind_label}: {formatDateDMY(item.starts_on)} — {formatDateDMY(item.ends_on)}
              </Typography>
            ))}
          </Box>
        ) : null}

        {!loading ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.45 }}>
            {t('vacations.studentCard.hint')}
          </Typography>
        ) : null}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('vacations.studentCard.add')}</DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('vacations.studentCard.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as AbsenceKind)}
          >
            {ABSENCE_KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`vacations.kind.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            type="date"
            label={t('vacations.studentCard.startsOn')}
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            fullWidth
            margin="dense"
            type="date"
            label={t('vacations.studentCard.endsOn')}
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleSave} sx={{ textTransform: 'none' }}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
